import type { QueryClient } from "@tanstack/react-query";
import {
  CHAT_ROOM_ALL,
  isMentionAll,
  type ChatConversationResponse,
  type ChatMessage,
  type ChatReactionSummary,
  type ChatRoomId,
  type ChatScope,
  type NotificationPreferencesResponse,
  type PresenceStatus,
} from "@xop/dashboard-shared";

import { toast } from "../../components/ui/use-toast.js";
import { deliverDesktopNotification, isTauri } from "../notifications/desktopBridge.js";
import { isAppFocused } from "../notifications/appFocus.js";
import { playNotificationSound } from "../notifications/sound.js";
import { showWebNotification } from "../notifications/webNotify.js";
import { getActiveChatConversation } from "./activeConversation.js";
import { chatKeys, messageConversationKey } from "./keys.js";

/**
 * Render a freshly-arrived chat message WITHOUT the Chat page having to wire
 * into the socket itself: append it to the (cached) open thread, refresh the
 * sidebar's server-derived unread counts, and — for someone ELSE's message —
 * surface it, *focus-gated*: a real OS desktop notification when the app is
 * NOT focused (minimised / hidden in the tray / backgrounded / on another
 * Space), an unobtrusive in-app snackbar when it IS focused, and nothing at
 * all if the user is already looking at that very conversation. Suppressed
 * under DND / global mute / for @mentions (which own a louder path).
 *
 * This mirrors `handleNotificationEvent` feeding the bell. Dependencies are
 * passed in so the routing logic is unit-tested without a real socket.
 */
export function handleChatMessage(
  qc: QueryClient,
  message: ChatMessage,
  selfId: number,
  status: PresenceStatus,
): void {
  const key = messageConversationKey(message, selfId);

  // Live-append to an open thread, deduped by id — the sender's own POST
  // already inserted the message, so its echo must not double it.
  qc.setQueryData<ChatConversationResponse>(chatKeys.conversation(key), (prev) =>
    !prev || prev.messages.some((m) => m.id === message.id)
      ? prev
      : { ...prev, messages: [...prev.messages, message] },
  );

  // Unread counts + roster ordering are server-derived — refetch them.
  void qc.invalidateQueries({ queryKey: chatKeys.overview });

  // Smart interruption — only for OTHERS' messages, when not DND and not
  // globally muted.
  if (message.sender_id === selfId || status === "dnd") return;
  // A message that @mentions me fires a louder `chat_mention` notification
  // (bell + desktop popup with sound + email) via the server. Skip the ambient
  // chat path so the viewer isn't interrupted twice. `@channel` mentions
  // EVERYONE, so it owns the louder path for every viewer — checking only the
  // literal id would double-interrupt the whole team. See docs/chat.md.
  if (message.mentions?.some((mn) => mn.user_id === selfId || isMentionAll(mn))) return;
  if (isMuted(qc)) return;

  // Focus-gated delivery (the operator's rule): when the app is focused and in
  // active use, an OS desktop notification is redundant — the user can see the
  // message. Only when the app is NOT focused (minimised, hidden in the tray,
  // in the background, or on another virtual desktop / Space) do we push a real
  // OS desktop notification, so a message is never missed while away. While
  // focused, an unobtrusive in-app snackbar is enough — and nothing at all if
  // the user is already looking at the very conversation it landed in.
  const focused = isAppFocused();
  if (focused && getActiveChatConversation() === key) return;

  // WhatsApp-style framing: DM → title is the sender; room → title is "All
  // Staff" with the sender prefixed into the body.
  const title = message.scope === "room" ? "All Staff" : message.sender_name;
  const body =
    message.scope === "room" ? `${message.sender_name}: ${message.body}` : message.body;

  if (focused) {
    // App in use (but not on this conversation) → ambient in-app snackbar; no
    // OS interruption while the user is actively working in the app.
    toast({ title, description: body, severity: "info" });
    return;
  }

  // App NOT focused → push an OS desktop notification WITH SOUND so the message
  // surfaces (and is heard) while minimised / backgrounded / closed-to-tray.
  if (isTauri()) {
    // OS-native banner (desktop app) — shown by the OS, so it appears even
    // while the window is minimised/backgrounded. `sound: true` plays the OS
    // notification sound. Falls back to the custom bottom-right popup (which
    // plays its own attention sound) on builds where the native banner can't
    // register (unsigned macOS).
    void deliverDesktopNotification({
      id: `chat:${message.id}`,
      title,
      body,
      severity: "info",
      sound: true,
    });
  } else {
    // Browser: a real OS notification when permission is granted, else the
    // ambient snackbar (unseen on a fully-hidden tab, but the unread badge
    // updates on return). Either way play the WebAudio chime so an unfocused
    // user hears it. Best-effort (autoplay policy) — never throws.
    if (!showWebNotification({ title, body, tag: `chat:${message.id}` })) {
      toast({ title, description: body, severity: "info" });
    }
    playNotificationSound();
  }
}

/**
 * Apply a live `chat_message_update` frame (an edit or delete) to the open
 * thread: REPLACE the message by id in its cached conversation, and refresh the
 * sidebar (a delete/edit can change the last-message preview + unread). A
 * no-op if that conversation isn't cached. (C3)
 */
export function handleChatMessageUpdate(qc: QueryClient, message: ChatMessage, selfId: number): void {
  const key = messageConversationKey(message, selfId);
  qc.setQueryData<ChatConversationResponse>(chatKeys.conversation(key), (prev) =>
    !prev
      ? prev
      : { ...prev, messages: prev.messages.map((m) => (m.id === message.id ? message : m)) },
  );
  void qc.invalidateQueries({ queryKey: chatKeys.overview });
}

/**
 * Apply a live `chat_reaction` frame to the open thread: find the message in
 * the cached conversation and replace its `reactions` with the new aggregated
 * set. A no-op if that conversation isn't currently cached (it'll be correct
 * on the next fetch). Routing mirrors `messageConversationKey`. (C2)
 */
export function handleChatReaction(
  qc: QueryClient,
  info: {
    message_id: number;
    scope: ChatScope;
    room: ChatRoomId | null;
    sender_id: number;
    recipient_id: number | null;
    reactions: ChatReactionSummary[];
  },
  selfId: number,
): void {
  const key =
    info.scope === "room"
      ? `room:${info.room ?? CHAT_ROOM_ALL}`
      : `dm:${(info.sender_id === selfId ? info.recipient_id : info.sender_id) ?? 0}`;
  qc.setQueryData<ChatConversationResponse>(chatKeys.conversation(key), (prev) =>
    !prev
      ? prev
      : {
          ...prev,
          messages: prev.messages.map((m) =>
            m.id === info.message_id ? { ...m, reactions: info.reactions } : m,
          ),
        },
  );
}

/** Respect the user's global notification mute for the loud chat interruption
 * (the in-app unread badge + thread still update regardless). */
function isMuted(qc: QueryClient): boolean {
  const prefs = qc.getQueryData<NotificationPreferencesResponse>(["notifications", "prefs"]);
  return prefs?.muted === true;
}
